/**
 * MobileAddDeal — Add / Suggest Deal · Coupon · Surplus form.
 *
 * Reached via the "+ Add/Suggest Deals/Coupons/Surplus" CTA on
 * /m/deals. Submits to ``POST /api/deals`` which persists to the
 * `fp_deals` collection. Every dropdown uses the shared
 * BottomSheetPicker — Deal Kind, Deal Type, Vendor (async from
 * food_businesses), State, City (async, state-filtered), Country.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Plus, X, Upload } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import BottomSheetPicker from "./BottomSheetPicker";
import { DEAL_TYPES, DEAL_KINDS } from "./dealsTaxonomy";
import { fetchCountries, fetchStates, fetchCities } from "./geoPickers";
import { Link } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL;
const FP_PURPLE = "#653091";
const FP_GREEN = "#A9D64C";

function Header({ title }) {
  const nav = useNavigate();
  return (
    <header
      className="sticky top-0 z-30 flex items-center gap-3 px-4 py-3"
      style={{ background: "linear-gradient(180deg,#A9D64C 0%,#B5DA5E 100%)" }}
    >
      <button onClick={() => nav(-1)} aria-label="Back" data-testid="add-deal-back"
        className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-white/20">
        <ArrowLeft className="w-5 h-5" />
      </button>
      <h1 className="text-xl font-bold">{title}</h1>
      <Link to="/food-pulse/vendor/import"
            data-testid="mobile-import-csv-btn"
            title="Bulk import from CSV"
            className="ml-auto inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white/25 text-sm font-semibold hover:bg-white/40">
        <Upload className="w-4 h-4" /> CSV
      </Link>
    </header>
  );
}

function Opener({ value, placeholder, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="w-full flex items-center justify-between border border-gray-300 rounded-xl px-4 py-3.5 text-left bg-white"
    >
      <span className={value ? "text-gray-900 text-base" : "text-gray-400 text-base"}>
        {value || placeholder}
      </span>
      <span className="opacity-40">▾</span>
    </button>
  );
}

function Label({ children, required = false }) {
  return (
    <label className="block text-base font-bold mt-4 mb-2">
      {children} {required && <span className="text-red-500">*</span>}
    </label>
  );
}

export default function MobileAddDeal() {
  const { user } = useAuth() || {};
  const nav = useNavigate();

  const [kind, setKind] = useState("offer");
  const [dealType, setDealType] = useState("");
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [vendorSlug, setVendorSlug] = useState("");
  const [vendorName, setVendorName] = useState("");
  const [country, setCountry] = useState("United States");
  const [state, setState] = useState("");
  const [city, setCity] = useState("");
  const [zip, setZip] = useState("");
  const [discountPct, setDiscountPct] = useState("");
  const [tagsRaw, setTagsRaw] = useState("");
  const [images, setImages] = useState([]);
  const [externalUrl, setExternalUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const [sheet, setSheet] = useState(null); // 'kind'|'type'|'vendor'|'country'|'state'|'city'

  const handleImages = (e) => {
    const files = Array.from(e.target.files || []).slice(0, 5);
    Promise.all(files.map((f) => new Promise((res) => {
      const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(f);
    }))).then((urls) => setImages((prev) => [...prev, ...urls].slice(0, 8)));
  };

  const submit = async () => {
    if (!user) {
      toast.error("Please log in to add a deal");
      nav("/login?next=%2Fm%2Fadd-deal"); return;
    }
    if (!title.trim() || title.trim().length < 3) { toast.error("Title required (min 3 chars)"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/api/deals`, {
        kind,
        deal_type: dealType || null,
        title: title.trim(),
        description: desc || null,
        vendor_slug: vendorSlug || null,
        vendor_name: vendorName || null,
        country,
        state: state || null,
        city: city || null,
        zip: zip || null,
        discount_pct: discountPct ? parseInt(discountPct, 10) : null,
        tags: tagsRaw.split(",").map((s) => s.trim()).filter(Boolean),
        image_urls: images,
        external_url: externalUrl || null,
      }, {
        headers: { Authorization: `Bearer ${user.token || ""}` },
      });
      toast.success("Deal submitted!");
      nav(`/m/deals`);
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Submit failed";
      toast.error(typeof msg === "string" ? msg : "Submit failed");
    } finally {
      setSubmitting(false);
    }
  };

  const kindLabel = DEAL_KINDS.find((k) => k.value === kind)?.label || "";
  const countryLabel = country;

  return (
    <div className="min-h-screen bg-white pb-10" data-testid="mobile-add-deal">
      <Header title="Add Deal / Coupon / Surplus" />

      <div className="px-4 pt-3 space-y-1">
        <Label required>Category</Label>
        <Opener value={kindLabel} placeholder="Select Category" onClick={() => setSheet("kind")} testId="deal-kind-opener" />

        <Label required>Deals Type</Label>
        <Opener value={dealType} placeholder="Select Deals Type" onClick={() => setSheet("type")} testId="deal-type-opener" />

        <Label required>Title</Label>
        <input
          value={title} onChange={(e) => setTitle(e.target.value)}
          data-testid="deal-title-input"
          placeholder="e.g. 50% off end-of-day produce"
          className="w-full border border-gray-300 rounded-xl px-4 py-3.5 outline-none text-base bg-white placeholder-gray-400"
        />

        <Label>Description</Label>
        <textarea
          value={desc} onChange={(e) => setDesc(e.target.value)}
          data-testid="deal-desc-input"
          rows={4}
          placeholder="What's the deal? When does it end?"
          className="w-full border border-gray-300 rounded-xl px-4 py-3.5 outline-none text-base bg-white placeholder-gray-400"
        />

        <Label>Vendor / Store</Label>
        <Opener value={vendorName} placeholder="Select Vendor" onClick={() => setSheet("vendor")} testId="deal-vendor-opener" />

        <Label>Country</Label>
        <Opener value={countryLabel} placeholder="Select Country" onClick={() => setSheet("country")} testId="deal-country-opener" />

        <Label>State</Label>
        <Opener value={state} placeholder="Select State" onClick={() => setSheet("state")} testId="deal-state-opener" />

        <Label>City</Label>
        <Opener value={city} placeholder="Select City" onClick={() => setSheet("city")} testId="deal-city-opener" />

        <Label>Zip / Pincode</Label>
        <input
          value={zip} onChange={(e) => setZip(e.target.value)}
          data-testid="deal-zip-input"
          placeholder="Enter Pincode"
          className="w-full border border-gray-300 rounded-xl px-4 py-3.5 outline-none text-base bg-white placeholder-gray-400"
        />

        <Label>Discount %</Label>
        <input
          value={discountPct} onChange={(e) => setDiscountPct(e.target.value.replace(/[^\d]/g, ""))}
          data-testid="deal-discount-input"
          placeholder="e.g. 50"
          inputMode="numeric"
          className="w-full border border-gray-300 rounded-xl px-4 py-3.5 outline-none text-base bg-white placeholder-gray-400"
        />

        <Label>Tags</Label>
        <input
          value={tagsRaw} onChange={(e) => setTagsRaw(e.target.value)}
          data-testid="deal-tags-input"
          placeholder="produce, dairy, bakery (comma-separated)"
          className="w-full border border-gray-300 rounded-xl px-4 py-3.5 outline-none text-base bg-white placeholder-gray-400"
        />

        <Label>External Link</Label>
        <input
          value={externalUrl} onChange={(e) => setExternalUrl(e.target.value)}
          data-testid="deal-url-input"
          placeholder="https://…"
          className="w-full border border-gray-300 rounded-xl px-4 py-3.5 outline-none text-base bg-white placeholder-gray-400"
        />

        <Label>Images</Label>
        <label className="border border-dashed border-gray-400 rounded-xl w-full flex flex-col items-center justify-center py-6 cursor-pointer" data-testid="deal-image-upload">
          <input type="file" accept="image/*" multiple onChange={handleImages} className="hidden" />
          <span className="text-sm">Add Deal Images</span>
          <Plus className="w-6 h-6 mt-2" />
        </label>
        {images.length > 0 && (
          <div className="mt-3 grid grid-cols-3 gap-2">
            {images.map((src, idx) => (
              <div key={idx} className="relative">
                <img src={src} alt={`upload-${idx}`} className="w-full h-20 object-cover rounded-lg" />
                <button onClick={() => setImages((prev) => prev.filter((_, i) => i !== idx))}
                  className="absolute top-0 right-0 w-6 h-6 bg-white/90 rounded-bl-lg flex items-center justify-center">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="pt-6">
          <button
            onClick={submit}
            disabled={submitting}
            data-testid="deal-submit-btn"
            className="w-full py-4 rounded-xl text-white text-lg font-semibold shadow disabled:opacity-50"
            style={{ background: FP_PURPLE }}
          >
            {submitting ? "Submitting…" : "Submit Deal"}
          </button>
        </div>
      </div>

      {/* Bottom sheets */}
      <BottomSheetPicker
        open={sheet === "kind"}
        title="Select Category"
        options={DEAL_KINDS}
        onSelect={(v) => setKind(v)}
        onClose={() => setSheet(null)}
        testIdPrefix="deal-kind"
      />
      <BottomSheetPicker
        open={sheet === "type"}
        title="Select Deals Type"
        options={DEAL_TYPES}
        onSelect={(v) => setDealType(v)}
        onClose={() => setSheet(null)}
        testIdPrefix="deal-type"
      />
      <BottomSheetPicker
        open={sheet === "country"}
        title="Select Country"
        fetchOptions={fetchCountries}
        onSelect={(v) => setCountry(v)}
        onClose={() => setSheet(null)}
        testIdPrefix="deal-country"
      />
      <BottomSheetPicker
        open={sheet === "state"}
        title="Select State"
        fetchOptions={(query) => fetchStates(country, query)}
        onSelect={(v) => { setState(v); setCity(""); }}
        onClose={() => setSheet(null)}
        testIdPrefix="deal-state"
      />
      <BottomSheetPicker
        open={sheet === "city"}
        title="Select City"
        fetchOptions={(query) => fetchCities(country, state, query)}
        onSelect={(v) => setCity(v)}
        onClose={() => setSheet(null)}
        testIdPrefix="deal-city"
      />
      <BottomSheetPicker
        open={sheet === "vendor"}
        title="Select Vendor"
        fetchOptions={async (q) => {
          const res = await axios.get(`${API}/api/deals/vendors`, {
            params: { q: q || undefined, limit: 30 },
          });
          return (res.data.items || []).map((v) => ({
            value: v.slug,
            label: v.name,
            subtitle: [v.city, v.state].filter(Boolean).join(", "),
          }));
        }}
        onSelect={(slug, name) => { setVendorSlug(slug); setVendorName(name); }}
        onClose={() => setSheet(null)}
        testIdPrefix="deal-vendor"
      />
    </div>
  );
}
